import { useCallback, useEffect, useState } from 'react';
import {
  lineTotalForParticipant,
  type ExperienceOptionPricing,
} from '@flyingmuse/shared';
import { getExperiencePricing } from '../lib/booking';

/**
 * Live per-option pricing for a product, used to keep the detail-page package
 * tables in sync with LinkTwist. Cached per product; degrades to null (caller
 * falls back to the static price string) on error or while loading.
 */
const cache = new Map<number, ExperienceOptionPricing[]>();
const inflight = new Map<number, Promise<ExperienceOptionPricing[]>>();
const listeners = new Set<() => void>();

function load(productId: number): Promise<ExperienceOptionPricing[]> {
  if (cache.has(productId)) return Promise.resolve(cache.get(productId)!);
  let p = inflight.get(productId);
  if (!p) {
    p = getExperiencePricing(productId)
      .then((res) => {
        cache.set(productId, res.options);
        listeners.forEach((l) => l());
        return res.options;
      })
      .catch((err) => {
        inflight.delete(productId);
        throw err;
      });
    inflight.set(productId, p);
  }
  return p;
}

type Status = 'idle' | 'loading' | 'ready' | 'error';

export interface UseExperiencePricing {
  /** Live total for `pax` participants of an option, or null if unavailable. */
  livePrice: (optionAlias: string, pax: number) => number | null;
  status: Status;
}

export function useExperiencePricing(productId?: number): UseExperiencePricing {
  const [, force] = useState(0);
  const [status, setStatus] = useState<Status>('idle');

  useEffect(() => {
    if (productId == null) return;
    if (cache.has(productId)) {
      setStatus('ready');
      return;
    }
    let active = true;
    setStatus('loading');
    load(productId)
      .then(() => active && (setStatus('ready'), force((n) => n + 1)))
      .catch(() => active && setStatus('error'));
    const onUpdate = () => active && force((n) => n + 1);
    listeners.add(onUpdate);
    return () => {
      active = false;
      listeners.delete(onUpdate);
    };
  }, [productId]);

  const options = productId != null ? cache.get(productId) : undefined;

  const livePrice = useCallback(
    (optionAlias: string, pax: number): number | null => {
      const opt = options?.find((o) => o.alias === optionAlias);
      if (!opt || opt.pricing.length === 0) return null;
      // Per-person/fixed options carry one pricing entry (the primary type).
      const total = lineTotalForParticipant(opt.pricing[0], pax);
      // LinkTwist sometimes returns a pricing entry with no tiers (no price set
      // for the near-term slots) — total comes back 0. Treat that as "no live
      // price" so the row degrades to the WhatsApp enquiry CTA, never "€0".
      return total > 0 ? total : null;
    },
    [options],
  );

  return { livePrice, status };
}
